import { ABCDCompanyOptions } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCompanyOptions";
import {
  ABCDLambdaFetch,
  awaitJSON,
  DagLambdaFetch,
  S3SignedUrlFetch,
} from "../../utils/fetch-utils";
import {
  ABCDCreative,
  GetABCDCompanyOptionsParams,
  GetABCDCompanyOptionsResponse,
  GetABCDCreativesParams,
  GetABCDCreativesResponse,
  SaveABCDCompanyOptionsBody,
  SaveABCDCreativeSettingsBody,
} from "@blisspointmedia/bpm-types/dist/ABCD";

interface EvaluatedFeature {
  category: string;
  criteria: string;
  detected: boolean;
  disabled: boolean;
  id: string;
  name: string;
}

export interface ABCDScoreObject {
  annotations_evaluation: {
    evaluated_features: EvaluatedFeature[];
  };
  video_uri: string;
}

export const getABCDCompanyOptions = async (company: string): Promise<ABCDCompanyOptions> => {
  const optionsResp = await ABCDLambdaFetch<GetABCDCompanyOptionsParams>("/getABCDCompanyOptions", {
    params: { company },
  });
  return await awaitJSON<GetABCDCompanyOptionsResponse>(optionsResp);
};

export const getABCDCreatives = async (company: string): Promise<Record<string, ABCDCreative>> => {
  const abcdCreativesResp = await ABCDLambdaFetch<GetABCDCreativesParams>("/getABCDCreatives", {
    params: { company },
  });
  return await awaitJSON<GetABCDCreativesResponse>(abcdCreativesResp);
};

export const getS3Scores = async (
  company: string,
  creatives: ABCDCreative[]
): Promise<Record<string, ABCDScoreObject>> => {
  const scoreMap: Record<string, ABCDScoreObject> = {};
  for (const creative of creatives) {
    if (!creative.hasScore) {
      continue;
    }
    try {
      const s3Path = `bpm-abcd-scores/${company}/${creative.creative_id}/assessment.json`;
      const response = await S3SignedUrlFetch(s3Path);
      if (!response.ok) {
        throw new Error(`Failed to fetch data from URL: ${s3Path} - Status: ${response.status}`);
      }
      scoreMap[creative.creative_id] = JSON.parse(await response.text());
    } catch (error) {
      console.error(`Unable to get ABCD data for ${company}. Error: ${error}`);
    }
  }
  return scoreMap;
};

export const getABCDScore = (
  score: ABCDScoreObject
): { presentFeatures: number; totalFeatures: number } => {
  const features = score.annotations_evaluation.evaluated_features;
  let totalFeatures = 0;
  let presentFeatures = 0;
  for (const feature of features) {
    totalFeatures += !feature.disabled ? 1 : 0;
    presentFeatures += !feature.disabled && feature.detected ? 1 : 0;
  }
  return { presentFeatures, totalFeatures };
};

export const updateABCDScore = async (
  company: string,
  creativeId: string,
  score: ABCDScoreObject
): Promise<void> => {
  const s3Path = `bpm-abcd-scores/${company}/${creativeId}/assessment.json`;
  await S3SignedUrlFetch(s3Path, {
    method: "PUT",
    body: JSON.stringify(score),
  });
};

export const updateABCDCompanyOptions = async (
  abcdCompanyOptions: ABCDCompanyOptions
): Promise<void> => {
  await ABCDLambdaFetch<SaveABCDCompanyOptionsBody>("/saveABCDCompanyOptions", {
    method: "POST",
    body: {
      abcdCompanyOptions,
    },
  });
};

export const updateABCDCreative = async (abcdCreativeSettings: ABCDCreative): Promise<void> => {
  await ABCDLambdaFetch<SaveABCDCreativeSettingsBody>("/saveABCDCreativeSettings", {
    method: "POST",
    body: {
      abcdCreativeSettings,
    },
  });
};

export const kickOffABCDRunner = async (creative: ABCDCreative): Promise<void> => {
  await DagLambdaFetch("/start_single_job", {
    method: "POST",
    body: {
      dag: "abcd-runner",
      task: "ABCD-RUNNER",
      task_name: "ABCD-RUNNER",
      override: true,
      extra: `"${JSON.stringify({
        company: creative.company,
        assets: [creative.filename],
      }).replace(/"/g, '\\"')}"`,
    },
  });
};
